import React, { createContext, useCallback, useContext, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useSidebarState } from "./hooks/useSidebarState";

const SidebarContext = createContext(null);
const SidebarLayoutContext = createContext(null);

export function SidebarProvider({ children }) {
    const location = useLocation();
    const { collapsed, openKeys, selectedKeys, handleCollapse, onOpenChange } = useSidebarState(location.pathname);

    const setCollapsed = useCallback(
        (nextCollapsed) => {
            const resolvedCollapsed = typeof nextCollapsed === "function" ? nextCollapsed(collapsed) : nextCollapsed;
            const normalizedCollapsed = Boolean(resolvedCollapsed);

            if (normalizedCollapsed === collapsed) {
                return;
            }

            handleCollapse(normalizedCollapsed);
        },
        [collapsed, handleCollapse],
    );

    const toggleSidebar = useCallback(() => {
        handleCollapse(!collapsed);
    }, [collapsed, handleCollapse]);

    const sidebarValue = useMemo(
        () => ({
            collapsed,
            toggleSidebar,
            setCollapsed,
        }),
        [collapsed, toggleSidebar, setCollapsed],
    );

    const sidebarLayoutValue = useMemo(
        () => ({
            collapsed,
            openKeys,
            selectedKeys,
            setCollapsed,
            handleCollapse,
            onOpenChange,
        }),
        [collapsed, openKeys, selectedKeys, setCollapsed, handleCollapse, onOpenChange],
    );

    return (
        <SidebarContext.Provider value={sidebarValue}>
            <SidebarLayoutContext.Provider value={sidebarLayoutValue}>{children}</SidebarLayoutContext.Provider>
        </SidebarContext.Provider>
    );
}

export function useSidebar() {
    const context = useContext(SidebarContext);

    if (!context) {
        throw new Error("useSidebar must be used within SidebarProvider");
    }

    return context;
}

export function useSidebarLayoutState() {
    const context = useContext(SidebarLayoutContext);

    if (!context) {
        throw new Error("useSidebarLayoutState must be used within SidebarProvider");
    }

    return context;
}
